import { useEffect } from 'react';

import Placeholder from '@tiptap/extension-placeholder';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import PropTypes from 'prop-types';
import { BsTypeBold, BsTypeItalic, BsTypeUnderline, BsListUl, BsListOl, BsLink45Deg } from 'react-icons/bs';

const TBtn = ({ active, onClick, children, title }) => (
	<button
		type="button"
		onClick={onClick}
		title={title}
		className={`p-1.5 rounded transition-colors ${active ? 'bg-primary-100 text-primary-700' : 'text-bgBase-800 hover:text-text hover:bg-gray-100'}`}
	>
		{children}
	</button>
);

TBtn.propTypes = { active: PropTypes.bool, onClick: PropTypes.func, children: PropTypes.node, title: PropTypes.string };

const EditorToolbar = ({ editor }) => {
	if (!editor) return null;

	const addLink = () => {
		const url = window.prompt('URL eingeben:');
		if (url) editor.chain().focus().setLink({ href: url }).run();
	};

	return (
		<div className="flex items-center gap-0.5 px-3 py-1.5 border-b border-gray-100 bg-gray-50/50">
			<TBtn
				active={editor.isActive('bold')}
				onClick={() => editor.chain().focus().toggleBold().run()}
				title="Fett"
			>
				<BsTypeBold className="w-3.5 h-3.5" />
			</TBtn>
			<TBtn
				active={editor.isActive('italic')}
				onClick={() => editor.chain().focus().toggleItalic().run()}
				title="Kursiv"
			>
				<BsTypeItalic className="w-3.5 h-3.5" />
			</TBtn>
			<TBtn
				active={editor.isActive('underline')}
				onClick={() => editor.chain().focus().toggleUnderline().run()}
				title="Unterstrichen"
			>
				<BsTypeUnderline className="w-3.5 h-3.5" />
			</TBtn>
			<div className="w-px h-4 bg-gray-200 mx-1" />
			<TBtn
				active={editor.isActive('bulletList')}
				onClick={() => editor.chain().focus().toggleBulletList().run()}
				title="Aufzählung"
			>
				<BsListUl className="w-3.5 h-3.5" />
			</TBtn>
			<TBtn
				active={editor.isActive('orderedList')}
				onClick={() => editor.chain().focus().toggleOrderedList().run()}
				title="Nummerierung"
			>
				<BsListOl className="w-3.5 h-3.5" />
			</TBtn>
			<div className="w-px h-4 bg-gray-200 mx-1" />
			<TBtn active={editor.isActive('link')} onClick={addLink} title="Link einfügen">
				<BsLink45Deg className="w-3.5 h-3.5" />
			</TBtn>
		</div>
	);
};

EditorToolbar.propTypes = { editor: PropTypes.object };

/**
 * Shared TipTap rich text editor (toolbar + content area) used by the template editor,
 * the signature manager, and the CRM email composer.
 *
 * Uncontrolled: `initialContent` seeds the editor once. Programmatic access (setContent,
 * getHTML, insertContent) goes through `editorRef` or `onEditorReady`, which receive the
 * TipTap editor instance — use `onEditorReady` (state) when effects must react to it.
 */
const RichTextEditor = ({
	initialContent = '',
	placeholder = '',
	minHeightClass = 'min-h-[200px]',
	editorRef,
	onEditorReady,
	onUpdate,
}) => {
	const editor = useEditor({
		extensions: [StarterKit.configure({ link: { openOnClick: false } }), Placeholder.configure({ placeholder })],
		content: initialContent,
		editorProps: {
			attributes: {
				class: `rich-text-content max-w-none ${minHeightClass} px-4 py-3 outline-none text-sm text-text`,
			},
		},
		onUpdate: ({ editor: ed }) => onUpdate?.(ed.getHTML(), ed.getText()),
	});

	useEffect(() => {
		if (editorRef) editorRef.current = editor;
		if (editor) onEditorReady?.(editor);
		// Unmount destroys the TipTap instance — hand the parent null so it never calls
		// commands on a destroyed editor (it gets the fresh instance on remount).
		return () => {
			if (editorRef) editorRef.current = null;
			onEditorReady?.(null);
		};
	}, [editor, editorRef, onEditorReady]);

	return (
		<div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
			<EditorToolbar editor={editor} />
			<EditorContent editor={editor} />
		</div>
	);
};

RichTextEditor.propTypes = {
	initialContent: PropTypes.string,
	placeholder: PropTypes.string,
	minHeightClass: PropTypes.string,
	editorRef: PropTypes.shape({ current: PropTypes.object }),
	onEditorReady: PropTypes.func,
	onUpdate: PropTypes.func,
};

export default RichTextEditor;
